import React, { useState } from 'react';
import { useCourseTechnologyList } from '@/hooks/queries/technology/useList';
import { useCourseTechnologyCreate, useCourseTechnologyUpdate, useCourseTechnologyDelete } from '@/hooks/mutations/technology';
import { CourseTechnology, CourseTechnologyInput } from '@/types/courseTechnologies';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Cpu } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const CourseTechnologiesPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState<CourseTechnology | null>(null);

  const [formData, setFormData] = useState<CourseTechnologyInput>({
    icon: '',
    courseId: '',
    translations: [
      { locale: 'uz', name: '' },
      { locale: 'ru', name: '' },
      { locale: 'en', name: '' },
    ],
  });

  const { data: techsData, isLoading, refetch } = useCourseTechnologyList({
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createTech, isPending: isCreating } = useCourseTechnologyCreate();
  const { mutate: updateTech, isPending: isUpdating } = useCourseTechnologyUpdate();
  const { mutate: deleteTech } = useCourseTechnologyDelete();

  const handleOpenCreate = () => {
    setSelectedTech(null);
    setFormData({
      icon: '',
      courseId: '',
      translations: [
        { locale: 'uz', name: '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (tech: CourseTechnology) => {
    setSelectedTech(tech);
    setFormData({
      icon: tech.icon || '',
      courseId: tech.courseId || '',
      translations: [
        { locale: 'uz', name: tech.name || '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, name: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTech) {
      updateTech(
        { id: selectedTech.id, data: { icon: formData.icon, translations: formData.translations } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Texnologiya yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Texnologiya yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createTech(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi texnologiya yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Texnologiya yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedTech) return;
    deleteTech(selectedTech.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Texnologiya o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedTech(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Texnologiya o'chirilmadi", variant: 'destructive' }),
    });
  };

  const techsList: CourseTechnology[] = techsData?.data?.data || techsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Texnologiyalari (Technologies)</h1>
          <p className="text-sm text-slate-500 mt-1">Kursda qo'llaniladigan va o'rgatiladigan texnologiyalar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi texnologiya
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : techsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Cpu className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha texnologiyalar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Ikonka</th>
                  <th className="px-4 py-3.5">Texnologiya Nomi</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {techsList.map((tech) => (
                  <tr key={tech.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {tech.icon ? (
                        <img src={getMediaUrl(tech.icon)} alt={tech.name} className="size-8 object-contain rounded border border-slate-100 p-1 bg-white" />
                      ) : (
                        <div className="size-8 rounded bg-blue-50 text-primary flex items-center justify-center font-bold">
                          <Cpu className="size-4" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{tech.name || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(tech)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedTech(tech);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={techsData?.meta?.pagination ?? techsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedTech ? 'Texnologiyani tahrirlash' : 'Yangi texnologiya yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <FileUploadInput
              label="Ikonka (Rasm)"
              value={typeof formData.icon === 'string' ? formData.icon : ''}
              onChange={(url) => setFormData({ ...formData, icon: url })}
            />

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { name: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Texnologiya nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.name || ''}
                          onChange={(e) => handleTranslationChange(locale, e.target.value)}
                          placeholder="NestJS / PostgreSQL"
                          className="rounded-xl"
                          required={locale === 'uz'}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Texnologiyani o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu texnologiya o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CourseTechnologiesPage;
